import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Approval.css";

const PAYMENT_API = "https://okna.co.tz/mrms/Payment.php";

export default function Approval() {
  const token = localStorage.getItem("token");
  const staff = JSON.parse(localStorage.getItem("staff") || "{}");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingPaymentId, setApprovingPaymentId] = useState(null);
  const [countdowns, setCountdowns] = useState({});

  // Check authentication on mount
  useEffect(() => {
    if (!token) {
      Swal.fire("Session Expired", "Please login again", "warning").then(() => {
        window.location.href = "/";
      });
      return;
    }
  }, [token]);

  const fetchPendingPayments = async () => {
    try {
      const res = await fetch(`${PAYMENT_API}?action=pending&token=${token}`, {
        method: "GET",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("staff");
        Swal.fire("Session Expired", "Please login again", "warning").then(() => {
          window.location.href = "/";
        });
        return;
      }

      if (res.ok) {
        const data = await res.json();
        console.log("Raw payment data from API:", data);
        
        // Handle different possible response structures
        let paymentsArray = [];
        if (Array.isArray(data)) {
          paymentsArray = data;
        } else if (data.payments && Array.isArray(data.payments)) {
          paymentsArray = data.payments;
        } else if (data.data && Array.isArray(data.data)) {
          paymentsArray = data.data;
        } else {
          console.error("Unexpected data structure:", data);
          paymentsArray = [];
        }
        
        // Filter only PENDING payments
        const pending = paymentsArray.filter(p => p.status === 'PENDING');
        console.log("Pending payments:", pending);
        
        setPayments(pending);
      } else {
        const error = await res.json();
        console.error("Fetch error:", error);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingPayments();
    const interval = setInterval(fetchPendingPayments, 3000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer for approved payments
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdowns(prev => {
        const newCountdowns = {};
        Object.entries(prev).forEach(([id, time]) => {
          if (time > 1) newCountdowns[id] = time - 1;
        });
        return newCountdowns;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const approvePayment = async (paymentId) => {
    console.log("Approving payment with ID:", paymentId);
    
    // Validate paymentId
    if (!paymentId && paymentId !== 0) {
      console.error("No payment ID provided. paymentId is:", paymentId);
      Swal.fire("Error", "Invalid payment ID. Please refresh and try again.", "error");
      return;
    }

    try {
      setApprovingPaymentId(paymentId);
      
      // Find the payment being approved
      const payment = payments.find(p => (p.id || p.payment_id) === paymentId);
      
      if (!payment) {
        console.error("Payment not found with ID:", paymentId);
        Swal.fire("Error", "Payment not found. Please refresh and try again.", "error");
        setApprovingPaymentId(null);
        return;
      }
      
      // Show confirmation dialog
      const confirm = await Swal.fire({
        title: 'Confirm Approval',
        html: `
          <div style="text-align: left;">
            <p><strong>Order #:</strong> ${payment.order_number || 'N/A'}</p>
            <p><strong>Amount:</strong> ${(payment.total_amount || 0).toLocaleString()} TZS</p>
            <p><strong>Payment Method:</strong> ${payment.payment_method || 'N/A'}</p>
            <p><strong>Waiter:</strong> ${payment.staff_name || 'N/A'}</p>
          </div>
          <p style="margin-top: 15px;">Verify payment received and approve?</p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: '✅ Approve',
        cancelButtonText: '❌ Cancel',
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#ef4444'
      });
      
      if (!confirm.isConfirmed) {
        setApprovingPaymentId(null);
        return;
      }
      
      // Send approval request
      const requestBody = JSON.stringify({ 
        action: "approve", 
        id: paymentId 
      });
      
      console.log("Sending approval request:", requestBody);
      
      const res = await fetch(`${PAYMENT_API}?token=${token}`, {
        method: "PUT",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json" 
        },
        body: requestBody
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("staff");
        Swal.fire("Session Expired", "Please login again", "warning").then(() => {
          window.location.href = "/";
        });
        return;
      }

      const data = await res.json();
      console.log("Approval response:", data);

      if (res.ok && (data.success || data.message)) {
        // Start countdown for this payment
        setCountdowns(prev => ({ ...prev, [paymentId]: 10 }));
        
        // Remove from UI immediately
        setPayments(prev => prev.filter(p => (p.id || p.payment_id) !== paymentId));
        
        Swal.fire({
          title: "✅ Payment Approved!",
          text: `Payment approved by ${data.approved_by || staff.full_name}`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end"
        });
        
        // Refresh list after 1 second
        setTimeout(() => {
          fetchPendingPayments();
          setApprovingPaymentId(null);
        }, 1000);
      } else {
        Swal.fire({
          title: "Approval Failed",
          text: data.error || "Failed to approve payment",
          icon: "error",
          confirmButtonColor: "#d33"
        });
        setApprovingPaymentId(null);
      }
    } catch (err) {
      console.error("Approve error:", err);
      Swal.fire({
        title: "Network Error",
        text: "Failed to approve payment. Please try again.",
        icon: "error",
        confirmButtonColor: "#d33"
      });
      setApprovingPaymentId(null);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-GB');
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      'CASH': '💰',
      'M-PESA': '📱', 
      'MIX BY YAS': '💳',
      'SELECOM': '🏦',
      'CRDB BANK': '🏛️'
    };
    return icons[method] || '💳';
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      'CASH': '#10b981',
      'M-PESA': '#3b82f6', 
      'MIX BY YAS': '#8b5cf6',
      'SELECOM': '#f59e0b',
      'CRDB BANK': '#ef4444'
    };
    return colors[method] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="approval-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading pending payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="approval-container">
      <header className="approval-header">
        <div className="header-left">
          <h1>💸 Payment Approval</h1>
          <div className="cashier-info">
            <span className="cashier-label">Cashier:</span>
            <span className="cashier-name">{staff.full_name}</span>
          </div>
        </div>
        <div className="header-right">
          <div className="pending-badge">
            <span className="badge-count">{payments.length}</span>
            <span className="badge-label">Pending</span>
          </div>
          <button className="refresh-btn" onClick={fetchPendingPayments}>
            🔄 Refresh
          </button>
        </div>
      </header>

      <div className="approval-list-container">
        {payments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">✅</div>
            <h3>No Pending Payments</h3>
            <p>All payments are cleared and approved</p>
            <button 
              className="refresh-button"
              onClick={fetchPendingPayments}
            >
              Check Again
            </button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="payments-table">
              <thead>
                <tr>
                  <th>ORDER #</th>
                  <th>DATE</th>
                  <th>TIME</th>
                  <th>WAITER</th>
                  <th>METHOD</th>
                  <th>AMOUNT</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => {
                  // Get the correct payment ID
                  const paymentId = payment.id || payment.payment_id;
                  const countdown = countdowns[paymentId] || 0;
                  const isApproving = countdown > 0 || approvingPaymentId === paymentId;
                  
                  return (
                    <tr key={paymentId} className={isApproving ? 'approving-row' : ''}>
                      <td className="order-cell">
                        <div className="order-number">#{payment.order_number || payment.order_no}</div>
                      </td>
                      
                      <td className="date-cell">
                        {formatDate(payment.created_at || payment.created_date)}
                      </td>
                      
                      <td className="time-cell">
                        {formatTime(payment.created_at || payment.created_date)}
                      </td>
                      
                      <td className="waiter-cell">
                        <div className="waiter-info">
                          <span className="waiter-avatar">
                            {(payment.staff_name || payment.waiter || "W").charAt(0)}
                          </span>
                          <span className="waiter-name">{payment.staff_name || payment.waiter || "Unknown"}</span>
                        </div>
                      </td>
                      
                      <td className="method-cell">
                        <div 
                          className="method-wrapper"
                          style={{ backgroundColor: `${getPaymentMethodColor(payment.payment_method)}15` }}
                        >
                          <span className="method-icon">
                            {getPaymentMethodIcon(payment.payment_method)}
                          </span>
                          <span className="method-text">{payment.payment_method}</span>
                        </div>
                      </td>
                      
                      <td className="amount-cell">
                        <div className="amount-main">
                          {(payment.total_amount || payment.amount || 0).toLocaleString()} TZS
                        </div>
                      </td>
                      
                      <td className="action-cell">
                        {isApproving ? (
                          <div className="approving-status">
                            <div className="approving-spinner"></div>
                            <span className="approving-text">Approving...</span>
                            {countdown > 0 && (
                              <span className="countdown-text">({countdown}s)</span>
                            )}
                          </div>
                        ) : (
                          <button 
                            className="approve-button"
                            onClick={() => approvePayment(paymentId)}
                            disabled={isApproving}
                          >
                            <span className="button-icon">✓</span>
                            <span className="button-text">Approve</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}